import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Header from '../../components/header/Header';
import Footer from '../../components/footer/Footer';
import LoginModal from '../../components/loginSection/LoginModal';
import { accountService } from '../../services';
import './ResetPassword.css';

const ResetPassword = () => {
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const openLoginModal = () => setIsLoginModalOpen(true);
    const closeLoginModal = () => setIsLoginModalOpen(false);
    const token = useMemo(() => searchParams.get('token') || '', [searchParams]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!token) {
            setError('This reset link is invalid or incomplete.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        if (password.length < 8) {
            setError('Please use a password with at least 8 characters.');
            return;
        }

        setIsSubmitting(true);

        try {
            await accountService.resetPassword(token, password);
            setSuccess(true);
            setTimeout(() => {
                navigate('/');
            }, 3000);
        } catch (err) {
            setError(err.message || 'Failed to reset password.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (success) {
        return (
            <div>
                <Header openLoginModal={openLoginModal} />
                <section className="reset-password-section">
                    <div className="reset-password-shell">
                        <div className="reset-password-card reset-password-card-success">
                            <div className="reset-password-icon success">✓</div>
                            <h2>Password reset successful</h2>
                            <p>Your password has been updated. Redirecting you to the home page now.</p>
                        </div>
                    </div>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div>
            <Header openLoginModal={openLoginModal} />
            <section className="reset-password-section">
                <div className="reset-password-shell">
                    <div className="reset-password-card">
                        <div className="reset-password-icon">SC</div>
                        <h2>Reset your password</h2>
                        <p className="reset-password-subtitle">Choose a new password for your Shoot Connect account.</p>

                        {!token ? (
                            <div className="reset-password-alert error">
                                This reset link is invalid or has expired. Please request a new one from the login screen.
                            </div>
                        ) : null}

                        {error ? <div className="reset-password-alert error">{error}</div> : null}

                        <form onSubmit={handleSubmit} className="reset-password-form">
                            <div className="form-field">
                                <label htmlFor="password" className="form-label">New Password <span>*</span></label>
                                <input
                                    type="password"
                                    id="password"
                                    className="text-field"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 8 characters"
                                    required
                                />
                            </div>
                            <div className="form-field">
                                <label htmlFor="confirmPassword" className="form-label">Confirm New Password <span>*</span></label>
                                <input
                                    type="password"
                                    id="confirmPassword"
                                    className="text-field"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    placeholder="Re-enter your new password"
                                    required
                                />
                            </div>
                            <div className="form-submit">
                                <button type="submit" className="default-btn" disabled={isSubmitting || !token}>
                                    {isSubmitting ? 'Resetting...' : 'Reset Password'}
                                </button>
                            </div>
                        </form>

                        <div className="form-links">
                            Remember your password? <button onClick={openLoginModal} className="login-link">Login here</button>
                        </div>
                    </div>
                </div>
            </section>
            <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
            <Footer />
        </div>
    );
};

export default ResetPassword;
